/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { getAssistant } from '@ibm-watson/shared/modules/ia/store/assistants/actions';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { extractErrorMessage } from '@ibm-watson/shared/modules/ia/util/rest';
import { IntegrationSchema } from '@ibm-watson/shared/ts/integration';
import { AsyncAppThunk, CreateAsyncThunk } from '@ibm-watson/shared/ts/state';
import { updatedObjectEvents } from '../../../ia/util/trackers/segment';
import * as integrationsApi from '../../api/integrations';
import {
  FIELDS as WEBCHAT_FIELDS,
  SUBSCRIBE_PAYLOAD_TRANSFORM as WEBCHAT_TRANSFORM,
  SUBSCRIBE_PAYLOAD_TRANSFORM_ONLY_GENERATED_KEY as WEBCHAT_TRANSFORM_ONLY_GENERATED_KEY
} from '../../components/web-chat/schema';
import { SUBSCRIBE_PAYLOAD_TRANSFORM as WHATSAPP_TRANSFORM } from '../../components/whatsapp/schema';
import {
  CATALOG_CHANNEL_TYPE,
  SEGMENT_EXTENSION_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  WHATSAPP_CHANNEL_TYPE
} from '../../util/channelTypes';
import { listCatalogItems, resetAllCatalogItems } from '../catalog/actions';
import { parseOpenAPIExtension } from '../parsedOpenAPIExtensions/actions';
import { messages } from '../service-desks/messages';
import {
  selectAllIntegrations,
  selectDraftIntegration,
  selectIntegrationById
} from './selectors';

const TIMEOUT = 60000; //60s millis

/**
 *
 *
 */
export const listIntegrations = createAsyncThunk<
  IntegrationSchema[],
  string,
  CreateAsyncThunk
>(
  'integrations/listIntegrations',
  async (assistantId, { dispatch }) => {
    try {
      const assistant = await dispatch(getAssistant({ assistantId })).unwrap();
      const environments = assistant.assistant_environments;
      // Below we must load the catalog items prior to the integrations so we
      // can ensure that we have all extensions that have been added to the
      // Assistant included
      await dispatch(listCatalogItems(assistantId));

      let integrations = [];
      for (const environment of environments) {
        const { environment_id } = environment;

        try {
          const integrationResponse = await integrationsApi.listIntegrations(
            assistantId,
            environment_id
          );
          if (!_.isNil(integrationResponse?.integrations)) {
            integrations = integrations.concat(
              integrationResponse.integrations
            );
          }
        } catch (error) {
          dispatch(addErrorMessage(error));
        }
      }

      for (const integration of integrations) {
        if (
          integration.type === CATALOG_CHANNEL_TYPE &&
          integration.name.toLowerCase() !== SEGMENT_EXTENSION_TYPE
        ) {
          await dispatch(parseOpenAPIExtension(integration));
        }
      }

      return integrations;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // List integrations ONLY IF it isn't already being called/processed.
    condition: (assistantId, { getState }) => {
      // use the pendingCallStatus to track per assistantId, return FALSE to block
      const pcs = getState().integrations?.listIntegrations?.pendingCallStatus;
      const lastStart = pcs?.[assistantId];
      return !lastStart || Date.now() - lastStart > TIMEOUT;
    }
  }
);

interface LoadCurrentIntegrationProps {
  assistantId: string;
  environmentId: string;
  integrationId: string;
  mergeDraft?: boolean;
}
/**
 *
 *
 */
export const loadCurrentIntegration = createAsyncThunk<
  any,
  LoadCurrentIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/loadIntegration',
  async (
    { assistantId, environmentId, integrationId, mergeDraft },
    { dispatch, getState }
  ) => {
    try {
      const integration = await integrationsApi.getIntegration(
        assistantId,
        environmentId,
        integrationId
      );

      if (mergeDraft === true) {
        const draftIntegration = selectDraftIntegration(getState());
        if (!_.isNil(draftIntegration?.settings)) {
          integration.settings = {
            ...draftIntegration.settings,
            ...integration.settings
          };
        }
      }

      return integration;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

interface CreateIntegrationProps {
  assistantId: string;
  environmentId: string;
  integration: Partial<IntegrationSchema>;
  markAsPremature?: boolean;
  skipLoad?: boolean;
}
/**
 *
 *
 */
export const createIntegration = createAsyncThunk<
  IntegrationSchema & { premature?: boolean },
  CreateIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/createIntegration',
  async (
    { assistantId, environmentId, integration, markAsPremature, skipLoad },
    { dispatch }
  ) => {
    // Temporary solution to solve an integrations backend bug.
    // When integration settings' private object is undefined, backend
    // will throw a NPE error when that integration's /subscribe
    // is called.
    if (
      !_.isEmpty(integration?.settings) &&
      _.isEmpty(integration?.settings?.private.secrets)
    ) {
      integration.settings.private.secrets = {};
    }

    try {
      const integrationResponse = await integrationsApi.createIntegration(
        assistantId,
        environmentId,
        integration
      );

      if (!skipLoad) {
        await dispatch(
          loadCurrentIntegration({
            assistantId,
            environmentId,
            integrationId: integrationResponse.integration_id,
            mergeDraft: Boolean(markAsPremature)
          })
        );
      }
      return integrationResponse;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

interface UpdateIntegrationProps {
  assistantId: string;
  environmentId: string;
  integrationId?: string;
  integration: Partial<IntegrationSchema>;
}
/**
 *
 *
 */
export const updateIntegration = createAsyncThunk<
  IntegrationSchema,
  UpdateIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/updateIntegration',
  async (
    { assistantId, environmentId, integrationId, integration },
    { dispatch }
  ) => {
    try {
      const safeIntegrationId = integrationId ?? integration.integration_id;
      const safeEnvironmentId = !_.isEmpty(environmentId)
        ? environmentId
        : integration.environment_id;
      const updatedIntegration = integrationsApi.updateIntegration(
        assistantId,
        safeEnvironmentId,
        safeIntegrationId,
        integration
      );
      updatedIntegration.catch(error => {
        const errorMessage = extractErrorMessage(error);
        if (errorMessage && errorMessage.startsWith('Unique Violation:')) {
          dispatch(
            addErrorMessage({
              message: errorMessage,
              status: 'error',
              transactionId: error?.transactionId
            })
          );
        } else {
          dispatch(addErrorMessage(error));
        }
        throw error;
      });
      // Register segment event updatedObjectEvents with successFlag true and resultValue is success
      updatedObjectEvents.integration(
        {
          object: integrationId ?? integration.integration_id,
          resultValue: 'success',
          successFlag: true
        },
        {
          waIntegrationId: integrationId ?? integration.integration_id
        }
      );
      return updatedIntegration;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      if (errorMessage && errorMessage.match(messages.uniqueViolation)) {
        const message = messages.createPhoneIntegrationFail;
        dispatch(
          addErrorMessage({
            message,
            status: 'error',
            transactionId: error?.transactionId
          })
        );
      } else {
        dispatch(addErrorMessage(error));
      }
      // Register updatedObjectEvents with successFlag false and resultValue is extractErrorMessage
      updatedObjectEvents.integration(
        {
          object: integrationId ?? integration.integration_id,
          resultValue: extractErrorMessage(error),
          successFlag: false
        },
        {
          waIntegrationId: integrationId ?? integration.integration_id
        }
      );
      throw error;
    }
  }
);

interface DeleteIntegrationProps {
  assistantId?: string;
  environmentId?: string;
  integrationId?: string;
  integration?: IntegrationSchema;
}
interface DeleteIntegrationPayload {
  integrationId: string;
  serviceDeskId?: string;
}
/**
 *
 *
 */
export const deleteIntegration = createAsyncThunk<
  DeleteIntegrationPayload,
  DeleteIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/deleteIntegration',
  async (
    { assistantId, environmentId, integrationId, integration },
    { dispatch, getState }
  ) => {
    try {
      const safeIntegrationId = integrationId ?? integration.integration_id;
      const safeIntegration =
        integration ?? selectIntegrationById(getState(), safeIntegrationId);
      const safeAssistantId = assistantId ?? safeIntegration.assistant_id;
      const safeEnvironmentId = environmentId ?? safeIntegration.environment_id;

      // Unsubscribe the integration, EXCEPT for extensions.
      // Extensions unsubscribe endpoint does not exist within the
      // extensions backend.
      // If unsubscribe fails, that's okay and we continue
      if (safeIntegration.type !== CATALOG_CHANNEL_TYPE) {
        try {
          await integrationsApi.unsubscribe(
            safeIntegration.type,
            safeIntegrationId
          );
        } catch (error) {
          console.error(error);
        }
      }

      const retObj = { integrationId: safeIntegrationId, serviceDeskId: null };

      // If channel is webchat, unsubscribe and delete service desk
      if (
        safeIntegration.type === WEBCHAT_CHANNEL_TYPE &&
        _.get(safeIntegration.settings, WEBCHAT_FIELDS.SERVICE_DESK.IS_ON) ===
          true &&
        !_.isNil(
          _.get(
            safeIntegration.settings,
            WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_TYPE
          )
        ) &&
        !_.isNil(
          _.get(
            safeIntegration.settings,
            WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_ID
          )
        )
      ) {
        retObj.serviceDeskId = _.get(
          safeIntegration.settings,
          WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_ID
        );

        try {
          await integrationsApi.unsubscribe(
            _.get(
              safeIntegration.settings,
              WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_TYPE
            ),
            retObj.serviceDeskId
          );
        } catch (error) {
          console.error(error);
        }

        await integrationsApi.deleteIntegration(
          safeAssistantId,
          safeEnvironmentId,
          retObj.serviceDeskId
        );
      }

      // Delete the integration
      await integrationsApi.deleteIntegration(
        safeAssistantId,
        safeEnvironmentId,
        safeIntegrationId
      );

      return retObj;
    } catch (error) {
      dispatch(addErrorMessage(error));
      // deletedObjectEvents
      throw error;
    }
  }
);
/**
 * This action will delete a given integration from all environments by type. Since we only allow one instance of each
 * integration by environment, this is an easy way to do cleanup. However, a catalog item is a special case! Since we
 * allow MANY different catalog items to be uploaded, we must also check if the catalogItemId matches to be sure we are
 * only removing the specific catalog item from all environments.
 *
 */
export const deleteAllIntegrationsByType = (
  channelType: string,
  catalogItemId?: string
): AsyncAppThunk<Promise<void>> => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const integrations = selectAllIntegrations(state);

      const integrationsToDelete = [];

      for (const integration of integrations) {
        if (integration.type !== channelType) {
          continue;
        }

        // If this is a catalog item, we don't want to delete ALL catalog items, just the ones with the same catalogItemId.
        if (catalogItemId) {
          if (integration.settings?.catalog_item_id === catalogItemId) {
            integrationsToDelete.push(
              dispatch(deleteIntegration({ integration }))
            );
          }
        } else {
          // For every other type of integration, we just throw them in the array to get processed.
          integrationsToDelete.push(
            dispatch(deleteIntegration({ integration }))
          );
        }
      }

      await Promise.all(integrationsToDelete);
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  };
};

interface SubscribeIntegrationProps {
  channelType: string;
  integrationId: string;
  data: any;
  isGenerateWebchatKey?: boolean;
  mergeResponse?: boolean;
}
/**
 *
 *
 */
export const subscribeIntegration = createAsyncThunk<
  any,
  SubscribeIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/subscribeIntegration',
  async (
    {
      channelType,
      integrationId,
      data = {},
      isGenerateWebchatKey = false,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mergeResponse = true // We do use this in the reducer.
    },
    { dispatch }
  ) => {
    try {
      const response = await integrationsApi.subscribe(
        channelType,
        integrationId,
        data,
        isGenerateWebchatKey
      );

      // Response payload transformation is needed when integrations
      // backend sometimes returns integrations values whose keys do
      // not match the keys defined by the integration schema. Thus,
      // the channels need a transform object, mapping the incorrect
      // key field to the correct one. We take that mapping, map the
      // values with their correct fields, and then merge the fields
      // with response.
      // Note that payload transformation is NOT necessary if either
      // of the following conditions are met:
      // - Integrations backend has not introduced any new fields or
      //   modified any existing fields. Here, the draftIntegration
      //   doesn't need to be updated with any changed fields.
      // - Integrations backend has mutated some fields, but the
      //   keys for all these fields are correct. In this case,
      //   updating draftIntegration would work correctly as normal.
      const transformPayloadChannels = [
        WEBCHAT_CHANNEL_TYPE,
        WHATSAPP_CHANNEL_TYPE
      ];
      if (transformPayloadChannels.includes(channelType)) {
        let transformObject;
        switch (channelType) {
          case WHATSAPP_CHANNEL_TYPE:
            transformObject = WHATSAPP_TRANSFORM;
            break;
          // Here we remove all fetched fields except for private.authentication.* so we don't override edits.
          case WEBCHAT_CHANNEL_TYPE:
            if (isGenerateWebchatKey) {
              transformObject = WEBCHAT_TRANSFORM_ONLY_GENERATED_KEY;
            } else {
              transformObject = WEBCHAT_TRANSFORM;
            }
            break;
          default:
            break;
        }

        return Object.keys(transformObject).reduce((resp, key) => {
          const payloadField = transformObject[key];
          _.set(resp, key, _.get(response, payloadField));
          return resp;
        }, {});
      }
      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

/**
 *
 */
export const disableSaveCreateButton = createAsyncThunk<
  boolean,
  boolean,
  CreateAsyncThunk
>('integrations/disableSaveCreateButton', async disable => disable);

export const setDraftIntegrationEnableSecretFields = createAction(
  'integration/setDraftIntegrationEnableSecretFields',
  enabled => ({
    payload: enabled
  })
);

export const setIntegrationInitializing = createAction(
  'integrations/setIntegrationInitializing',
  enabled => ({
    payload: enabled
  })
);

/**
 *
 *
 */
export const resetCurrentIntegration = createAction(
  'integration/resetCurrentIntegration'
);

export const resetAllIntegrations = createAction(
  'integration/resetAllIntegrations'
);

/**
 * Singular method used to reset all integrations resources.
 */
export const resetAllIntegrationsResources = (): AsyncAppThunk<
  Promise<void>
> => {
  return async dispatch => {
    await dispatch(resetAllIntegrations());
    await dispatch(resetAllCatalogItems());
  };
};
